// Decides whether a lesson counts as "completed" for XP/effort-tracking
// purposes. Pulled out of LecturePlayer.tsx so the decision is testable
// without mounting the player (audio element, gating, panels, etc).
//
// Why this exists (the diagnosed bug): the old trigger fired only from
// AudioController's onAudioEnded on the LAST slide, so a lesson completed
// only if the student let every slide's narration play to the end. A
// student who reads faster than the audio, skips a slide, or navigates away
// never completed a lesson — XP stayed 0 forever, which is what the
// instructor saw as "the 0 XP icon doesn't work".
//
// "Completed" now means: every slide in the lesson has been genuinely
// engaged with, where engagement is EITHER of —
//   1. the existing "covered" signal (audio finished, or a gating widget
//      answered) — unchanged, still the signal SlideProgressRail's
//      advance-when-covered gating uses, so this never alters playback or
//      navigation behaviour;
//   2. a minimum dwell time as the current slide (MIN_SLIDE_DWELL_MS),
//      tracked locally in LecturePlayer and NOT written to the shared
//      completedSlides/gating state.
//
// (2) is what makes completion reachable for a student who reads instead of
// listens. It is deliberately a per-slide *time* floor rather than an
// instant "slide was shown" credit, so clicking straight through the deck
// does not fake engagement — each slide still has to be visited long enough
// to plausibly have been read. Any slide gated `on_widget`/`on_audio_end`
// cannot be advanced past without satisfying its own gate regardless of
// dwell (SlideProgressRail's isLocked() never looks at this module), so a
// real quiz/widget check embedded in the lecture still has to be answered
// to reach the slides after it — "activities answered" is folded in through
// that unchanged gating rather than reimplemented here.
export const MIN_SLIDE_DWELL_MS = 4000;

/**
 * True once every slide id in `slideIds` appears in `coveredSlideIds`
 * (the existing audio-ended/widget-complete signal) or `dwellCoveredIds`
 * (the reading fallback). Empty lessons are never "complete" — there is
 * nothing to have engaged with.
 */
export function isLessonFullyEngaged(
  slideIds: string[],
  coveredSlideIds: string[],
  dwellCoveredIds: Iterable<string>
): boolean {
  if (slideIds.length === 0) return false;
  const dwellSet = dwellCoveredIds instanceof Set ? dwellCoveredIds : new Set(dwellCoveredIds);
  const coveredSet = new Set(coveredSlideIds);
  return slideIds.every((id) => coveredSet.has(id) || dwellSet.has(id));
}
